import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AppNav from '../../components/app/AppNav';
import GroupCard from '../../components/app/GroupCard';
import CommunityPicker from '../../components/app/CommunityPicker';
import { Group } from '../../types/groups';
import { GROUP_CATEGORIES, CATEGORY_EMOJI } from '../../constants/groups';
import { apiGet } from '../../api/api';
import { useAuth } from '../../context/AuthContext';
import { useCommunity } from '../../hooks/useCommunity';
import { useUserLocation } from '../../hooks/useUserLocation';

function SkeletonCard() {
  return (
    <div
      style={{
        backgroundColor: '#ffffff',
        border: '1.5px solid #eef0f6',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
      }}
    >
      <div
        style={{
          width: '100%',
          aspectRatio: '16/9',
          backgroundColor: '#f0f2f7',
          animation: 'shimmer 1.5s ease-in-out infinite',
        }}
      />
      <div style={{ padding: '1rem' }}>
        <div
          style={{
            height: '16px',
            backgroundColor: '#f0f2f7',
            borderRadius: '6px',
            marginBottom: '0.6rem',
            width: '65%',
            animation: 'shimmer 1.5s ease-in-out infinite',
          }}
        />
        <div
          style={{
            height: '12px',
            backgroundColor: '#f0f2f7',
            borderRadius: '6px',
            marginBottom: '0.4rem',
            animation: 'shimmer 1.5s ease-in-out infinite',
          }}
        />
        <div
          style={{
            height: '12px',
            backgroundColor: '#f0f2f7',
            borderRadius: '6px',
            width: '75%',
            animation: 'shimmer 1.5s ease-in-out infinite',
          }}
        />
      </div>
    </div>
  );
}

export default function AppGroups() {
  const navigate = useNavigate();
  const { authUser } = useAuth();
  const { communityInfo, saveCommunity } = useCommunity();
  const userCoords = useUserLocation();
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showPicker, setShowPicker] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [sortBy, setSortBy] = useState('members');

  const SORT_OPTIONS = [
    { value: 'members', label: 'Most Members' },
    { value: 'created_date', label: 'Newest' },
  ];

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const loadGroups = (community?: string, sort?: string) => {
    setLoading(true);
    setError(null);
    const currentSort = sort ?? sortBy;
    const params: Record<string, string> = {};
    if (!authUser && community) params.community = community;
    params.sortBy = currentSort;
    if (currentSort === 'distance' && userCoords) { params.userLat = String(userCoords.lat); params.userLng = String(userCoords.lng); }
    apiGet<{ data: Group[] }>(authUser ? '/api_groups' : '/api_groups-public', params, !!authUser)
      .then((res) => setGroups(res.data || []))
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Failed to load groups';
        if (msg.includes('Community not found')) {
          if (authUser) { navigate('/settings/profile'); return; }
          setShowPicker(true);
          return;
        }
        setError(msg);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (authUser) { loadGroups(); return; }
    if (communityInfo) { loadGroups(communityInfo.community); return; }
    setShowPicker(true);
    setLoading(false);
  }, [authUser]);

  const handleSortChange = (sort: string) => {
    setSortBy(sort);
    const community = communityInfo?.community;
    loadGroups(authUser ? undefined : community, sort);
  };

  const filtered = useMemo(() => {
    return groups.filter((g) => {
      const matchesCategory = activeCategory === 'All' || g.category === activeCategory;
      const q = search.toLowerCase();
      const matchesSearch =
        !q ||
        g.name.toLowerCase().includes(q) ||
        g.description.toLowerCase().includes(q) ||
        g.category.toLowerCase().includes(q) ||
        (g.city || '').toLowerCase().includes(q);
      return matchesCategory && matchesSearch;
    });
  }, [groups, activeCategory, search]);

  const handleCommunityConfirm = (nationality: string, city: string) => {
    const info = saveCommunity(nationality, city);
    setShowPicker(false);
    loadGroups(info.community, sortBy);
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f7f7f7' }}>
      {showPicker && <CommunityPicker onConfirm={handleCommunityConfirm} />}
      <AppNav
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by name, category, or city..."
      />

      {/* Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '1rem 1.25rem 3rem' }}>
        {/* Category filter strip + actions */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '0.75rem',
          }}
        >
          <div
            style={{
              flex: 1,
              overflowX: 'auto',
              display: 'flex',
              gap: '0.5rem',
              scrollbarWidth: 'none',
              paddingBottom: '2px',
            }}
          >
            {['All', ...GROUP_CATEGORIES].map((cat) => {
              const active = activeCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  style={{
                    flexShrink: 0,
                    padding: '0.45rem 1rem',
                    borderRadius: '20px',
                    border: active ? '1.5px solid #FF385C' : '1.5px solid #e2e6f0',
                    backgroundColor: active ? '#FF385C' : '#ffffff',
                    color: active ? '#ffffff' : '#4a5568',
                    fontSize: '0.82rem',
                    fontWeight: active ? 600 : 500,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.15s',
                    boxShadow: active ? '0 2px 8px rgba(255,56,92,0.25)' : '0 1px 4px rgba(0,0,0,0.04)',
                  }}
                >
                  {cat !== 'All' && CATEGORY_EMOJI[cat] + ' '}{cat}
                </button>
              );
            })}
          </div>

          {!isMobile && (
            <button
              onClick={() => navigate('/groups/create')}
              style={{
                flexShrink: 0,
                padding: '0.5rem 1.125rem',
                backgroundColor: '#1a1f36',
                border: 'none',
                borderRadius: '20px',
                color: '#ffffff',
                fontWeight: 600,
                fontSize: '0.85rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem',
                transition: 'all 0.15s',
                boxShadow: '0 2px 8px rgba(26,31,54,0.2)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#FF385C';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(255,56,92,0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#1a1f36';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(26,31,54,0.2)';
              }}
            >
              + Create Group
            </button>
          )}
        </div>

        {/* Sort strip */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', overflowX: 'auto', scrollbarWidth: 'none' }}>
          <span style={{ color: '#8896ab', fontSize: '0.78rem', flexShrink: 0 }}>↕</span>
          {SORT_OPTIONS.map((opt) => {
            const active = sortBy === opt.value;
            return (
              <button key={opt.value} onClick={() => handleSortChange(opt.value)} style={{ flexShrink: 0, padding: '0.3rem 0.875rem', borderRadius: '20px', border: active ? '1.5px solid #1a1f36' : '1.5px solid #e2e6f0', backgroundColor: active ? '#1a1f36' : '#ffffff', color: active ? '#ffffff' : '#4a5568', fontSize: '0.78rem', fontWeight: active ? 600 : 500, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s' }}>
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* Count */}
        {!loading && !error && (
          <p style={{ color: '#8896ab', fontSize: '0.82rem', margin: '0 0 0.75rem' }}>
            {filtered.length} {filtered.length === 1 ? 'group' : 'groups'} found
          </p>
        )}

        {error && (
          <div
            style={{
              padding: '1rem',
              backgroundColor: '#fff5f5',
              border: '1px solid #fed7d7',
              borderRadius: '10px',
              color: '#c53030',
              marginBottom: '1.5rem',
              fontSize: '0.9rem',
            }}
          >
            {error}
          </div>
        )}

        {loading ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))',
              gap: '1.25rem',
            }}
          >
            {Array.from({ length: 9 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '5rem 1rem' }}>
            <div style={{ fontSize: '3.5rem', marginBottom: '1rem' }}>🔍</div>
            <h3 style={{ color: '#1a1f36', fontWeight: 700, margin: '0 0 0.5rem', fontSize: '1.25rem' }}>
              No groups found
            </h3>
            <p style={{ color: '#8896ab', fontSize: '0.9rem', margin: '0 0 1.5rem' }}>
              {search || activeCategory !== 'All'
                ? 'Try different filters or clear your search.'
                : 'No groups yet. Be the first to create one!'}
            </p>
            {!search && activeCategory === 'All' && (
              <button
                onClick={() => navigate('/groups/create')}
                style={{
                  padding: '0.75rem 1.75rem',
                  backgroundColor: '#FF385C',
                  border: 'none',
                  borderRadius: '12px',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                }}
              >
                Create First Group
              </button>
            )}
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))',
              gap: '1.25rem',
            }}
          >
            {filtered.map((group) => (
              <GroupCard key={group.id} group={group} userCoords={userCoords} />
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes shimmer {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.55; }
        }
      `}</style>
    </div>
  );
}
